import React, { useState } from 'react';
import Layout from '../../components/Layout';
import Button from '../../components/Button';
import { truckingReportExtAPI } from '../../api/waybillAPI';

// PSACC SOA Registry — both SOA numbers a Trucking bill produces, side by side
// and in date order: the bill's own number (PSACC Billing) and the derived
// additional-stripping number (PSACC Strip, = bill number + 1). The point of
// the view is the sequence itself, so it stays chronological and unfiltered by
// client — PSACC is the only client on this billing type.

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return `${String(dt.getMonth() + 1).padStart(2, '0')}/${String(dt.getDate()).padStart(2, '0')}/${dt.getFullYear()}`;
};

const today = () => new Date().toISOString().slice(0, 10);
const startOfYear = () => `${new Date().getFullYear()}-01-01`;

const STATUS_STYLE = {
  paid:           'bg-success-50 text-success-700',
  partially_paid: 'bg-primary-50 text-primary-700',
  sent:           'bg-primary-50 text-primary-700',
  overdue:        'bg-warning-50 text-warning-700',
  voided:         'bg-danger-50 text-danger-700',
  draft:          'bg-ink-canvas text-ink-body',
};

function StatusBadge({ status }) {
  const style = STATUS_STYLE[status] || STATUS_STYLE.draft;
  return (
    <span className={`text-xs px-2 py-0.5 rounded-md ${style}`}>
      {String(status || 'draft').replace('_', ' ')}
    </span>
  );
}

// Issued vs. not-yet-issued is the thing a user scans this column for, so it
// carries the only other color on the page.
function StripCell({ row }) {
  if (row.derivedError) {
    return <span className="text-xs text-danger-700">Bill SOA is not YYYY-NNN</span>;
  }
  if (row.stripIssued) {
    return (
      <div className="flex items-center gap-2">
        <span className="font-mono">{row.stripSOANumber}</span>
        <span className="text-xs px-2 py-0.5 rounded-md bg-success-50 text-success-700">Issued</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <span className="font-mono text-ink-body">{row.stripSOANumber}</span>
      {row.stripConflict ? (
        <span
          className="text-xs px-2 py-0.5 rounded-md bg-danger-50 text-danger-700"
          title="This number is already held by another bill or statement. Printing the stripping statement will be blocked until it is resolved."
        >
          Conflict
        </span>
      ) : (
        <span className="text-xs px-2 py-0.5 rounded-md bg-ink-canvas text-ink-body" title="Reserved when the stripping statement is first printed.">
          Not issued
        </span>
      )}
    </div>
  );
}

function exportCSV(rows) {
  const header = [
    'Bill Date', 'Created', 'Bill Name', 'Bill SOA No.', 'Strip SOA No.', 'Strip Status', 'Bill Status',
  ];
  const body = rows.map((r) => [
    fmtDate(r.billDate),
    fmtDate(r.createdDate),
    r.billName || '',
    r.billSOANumber || '',
    r.derivedError ? '' : (r.stripSOANumber || ''),
    r.derivedError ? 'invalid bill SOA' : r.stripIssued ? 'issued' : r.stripConflict ? 'conflict' : 'not issued',
    r.isVoided ? 'voided' : (r.status || 'draft'),
  ]);
  const csv = [header, ...body]
    .map((cols) => cols.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `psacc-soa-registry-${today()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function PSACCSOARegistryPage() {
  const [dateFrom, setDateFrom] = useState(startOfYear());
  const [dateTo, setDateTo] = useState(today());
  const [includeVoided, setIncludeVoided] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await truckingReportExtAPI.getPSACCSOARegistry({
        dateFrom,
        dateTo,
        ...(includeVoided ? { includeVoided: 'true' } : {}),
      });
      setRows(data.rows || []);
      setSummary(data.summary || null);
      setLoaded(true);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load SOA registry');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout pageTitle="PSACC SOA registry">
      <div className="bg-white border border-ink-line rounded-xl p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs text-ink-body mb-1">Date from</label>
          <input
            type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            className="border border-ink-line rounded-md px-2 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div>
          <label className="block text-xs text-ink-body mb-1">Date to</label>
          <input
            type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            className="border border-ink-line rounded-md px-2 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-ink-body cursor-pointer pb-1.5">
          <input
            type="checkbox" checked={includeVoided}
            onChange={(e) => setIncludeVoided(e.target.checked)}
            className="h-4 w-4 rounded-md accent-primary-500"
          />
          Include voided
        </label>
        <Button onClick={load} loading={loading} size="sm">
          {loading ? 'Loading' : 'Load registry'}
        </Button>
        {loaded && rows.length > 0 && (
          <Button onClick={() => exportCSV(rows)} variant="outline" size="sm">
            Export CSV
          </Button>
        )}
      </div>

      {error && (
        <div className="mb-3 rounded-md border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700">
          {error}
        </div>
      )}

      {loaded && summary && (
        <div className="mb-4 flex flex-wrap gap-6 text-sm text-ink-body">
          <span>{summary.billCount} bill{summary.billCount === 1 ? '' : 's'}</span>
          <span>{summary.stripIssuedCount} stripping statement{summary.stripIssuedCount === 1 ? '' : 's'} issued</span>
          <span>{summary.stripPendingCount} not yet issued</span>
          {summary.conflictCount > 0 && (
            <span className="text-danger-700">{summary.conflictCount} number conflict{summary.conflictCount === 1 ? '' : 's'}</span>
          )}
        </div>
      )}

      {loaded && (
        <div className="bg-white border border-ink-line rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-ink-canvas text-left">
                  <th className="px-3 py-2 text-xs font-medium text-ink-body uppercase tracking-wide">Bill date</th>
                  <th className="px-3 py-2 text-xs font-medium text-ink-body uppercase tracking-wide">Created</th>
                  <th className="px-3 py-2 text-xs font-medium text-ink-body uppercase tracking-wide">Bill</th>
                  <th className="px-3 py-2 text-xs font-medium text-ink-body uppercase tracking-wide">Bill SOA no.</th>
                  <th className="px-3 py-2 text-xs font-medium text-ink-body uppercase tracking-wide">Strip SOA no.</th>
                  <th className="px-3 py-2 text-xs font-medium text-ink-body uppercase tracking-wide">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-ink-body">
                      No SOA numbers issued in this range.
                    </td>
                  </tr>
                ) : rows.map((r) => (
                  <tr key={r.billId} className="border-t border-ink-line">
                    <td className="px-3 py-2 text-ink">{fmtDate(r.billDate)}</td>
                    <td className="px-3 py-2 text-ink-body">{fmtDate(r.createdDate)}</td>
                    <td className="px-3 py-2 text-ink">{r.billName || '—'}</td>
                    <td className="px-3 py-2 font-mono text-ink">{r.billSOANumber}</td>
                    <td className="px-3 py-2"><StripCell row={r} /></td>
                    <td className="px-3 py-2"><StatusBadge status={r.isVoided ? 'voided' : r.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Layout>
  );
}
